/**
 * SEO utility functions for landing pages (events/memberships)
 *
 * These functions generate SEO metadata for detail pages.
 * They mirror the logic from the legacy DetailPageLayoutHelpers.ts
 */
import moment from "moment-timezone";
import { enums } from "@shared/constants";
import { isPastEvent } from "@shared/modules/events/utils/event-utils";
import type { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import type { PublicMembershipGroupSerializer } from "@shared/modules/memberships/types";
import type { SEOData } from "../adapters/types";

interface EventSEOParams {
  eventData?: PublicBaseEventSerializer;
}

interface MembershipSEOParams {
  membershipData?: PublicMembershipGroupSerializer;
}

/**
 * Generates SEO title for event pages
 */
export function getEventSEOTitle(
  eventData?: PublicBaseEventSerializer
): string {
  if (
    !eventData?.name ||
    !eventData?.location ||
    !eventData?.local_starts_on ||
    !eventData?.timezone
  ) {
    return "";
  }

  const locationCity = eventData.location?.city
    ? ` - ${eventData.location.city}`
    : "";

  return `${eventData.name} - ${
    eventData.location.name
  }${locationCity} - ${moment
    .tz(eventData.local_starts_on, eventData.timezone)
    .format("MMM D, Y")}`;
}

/**
 * Generates SEO title for membership pages
 */
export function getMembershipSEOTitle(
  membershipData?: PublicMembershipGroupSerializer
): string {
  if (!membershipData?.name || !membershipData?.venue?.name) {
    return "";
  }

  return `${membershipData.name} - ${membershipData.venue.name}`;
}

/**
 * Generates SEO description from event or membership data
 */
export function getSEODescription(
  params: EventSEOParams | MembershipSEOParams
): string {
  if ("eventData" in params && params.eventData) {
    return params.eventData.description_without_html ?? "";
  }

  if ("membershipData" in params && params.membershipData) {
    return params.membershipData.description_without_html ?? "";
  }

  return "";
}

/**
 * Determines if SEO should be disabled (noindex, nofollow)
 *
 * For events: disabled if past, unpublished, or child event
 * For memberships: disabled if not published
 */
export function shouldDisableSEO(
  params: EventSEOParams | MembershipSEOParams
): boolean {
  if ("eventData" in params) {
    if (!params.eventData) return true;
    return (
      isPastEvent(params.eventData) ||
      !params.eventData.is_published ||
      !!params.eventData.parent_event
    );
  }

  if ("membershipData" in params) {
    if (!params.membershipData) return true;
    return (
      params.membershipData.status !==
      enums.MEMBERSHIP_GROUP_STATUS_MAP.PUBLISHED.id
    );
  }

  return true;
}

/**
 * Builds complete SEO data for event pages (shape expected by PublicPage/SEO)
 */
export function buildEventSEOData(
  eventData?: PublicBaseEventSerializer
): SEOData {
  const description = getSEODescription({ eventData });
  return {
    title: getEventSEOTitle(eventData),
    description,
    metaDescription: description,
    image: eventData?.image_banner ?? undefined,
    shouldDisableSEO: shouldDisableSEO({ eventData }),
  };
}

/**
 * Builds complete SEO data for membership pages (shape expected by PublicPage/SEO)
 */
export function buildMembershipSEOData(
  membershipData?: PublicMembershipGroupSerializer
): SEOData {
  const description = getSEODescription({ membershipData });
  return {
    title: getMembershipSEOTitle(membershipData),
    description,
    metaDescription: description,
    image: membershipData?.image ?? undefined,
    shouldDisableSEO: shouldDisableSEO({ membershipData }),
  };
}
